import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  IconButton,
  Collapse,
  Alert
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  Create as CreateIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';

function AuditLogs() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');
  const [expandedRows, setExpandedRows] = useState({});

  // Statistics
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [page, rowsPerPage, entityType, action]);

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        skip: page * rowsPerPage,
        limit: rowsPerPage
      };

      if (entityType) params.entity_type = entityType;
      if (action) params.action = action;

      const response = await axios.get('/audit/', {
        params
      });

      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError('Ошибка при загрузке логов');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/audit/stats');
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleRowExpansion = (logId) => {
    setExpandedRows(prev => ({
      ...prev,
      [logId]: !prev[logId]
    }));
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'create':
        return <CreateIcon fontSize="small" />;
      case 'update':
        return <EditIcon fontSize="small" />;
      case 'delete':
        return <DeleteIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'create':
        return 'success';
      case 'update':
        return 'info';
      case 'delete':
        return 'error';
      default:
        return 'default';
    }
  };

  const getActionLabel = (action) => {
    switch (action) {
      case 'create':
        return 'Создание';
      case 'update':
        return 'Изменение';
      case 'delete':
        return 'Удаление';
      default:
        return action;
    }
  };

  const getEntityTypeLabel = (entityType) => {
    const labels = {
      'device': 'Устройство',
      'connection': 'Подключение',
      'location': 'Локация',
      'device_type': 'Тип устройства',
      'characteristic': 'Характеристика',
      'user': 'Пользователь',
      'subnet': 'Подсеть'
    };
    return labels[entityType] || entityType;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const renderChanges = (changes) => {
    if (!changes || Object.keys(changes).length === 0) {
      return <Typography variant="body2" color="textSecondary">Нет изменений</Typography>;
    }

    return (
      <Box sx={{ mt: 1 }}>
        {Object.entries(changes).map(([field, change]) => (
          <Box key={field} sx={{ mb: 1 }}>
            <Typography variant="body2" component="span" sx={{ fontWeight: 'bold' }}>
              {field}:
            </Typography>
            {change.old !== undefined && (
              <Typography variant="body2" component="span" sx={{ ml: 1, color: 'error.main' }}>
                {change.old === null ? '(пусто)' : String(change.old)}
              </Typography>
            )}
            <Typography variant="body2" component="span" sx={{ mx: 1 }}>
              →
            </Typography>
            {change.new !== undefined && (
              <Typography variant="body2" component="span" sx={{ color: 'success.main' }}>
                {change.new === null ? '(пусто)' : String(change.new)}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        История изменений
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Всего записей
                </Typography>
                <Typography variant="h4">
                  {stats.total_logs}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Создано
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.by_action.find(a => a.action === 'create')?.count || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Изменено
                </Typography>
                <Typography variant="h4" color="info.main">
                  {stats.by_action.find(a => a.action === 'update')?.count || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Удалено
                </Typography>
                <Typography variant="h4" color="error.main">
                  {stats.by_action.find(a => a.action === 'delete')?.count || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              fullWidth
              label="Тип сущности"
              value={entityType}
              onChange={(e) => {
                setEntityType(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">Все</MenuItem>
              <MenuItem value="device">Устройства</MenuItem>
              <MenuItem value="connection">Подключения</MenuItem>
              <MenuItem value="location">Локации</MenuItem>
              <MenuItem value="device_type">Типы устройств</MenuItem>
              <MenuItem value="characteristic">Характеристики</MenuItem>
              <MenuItem value="subnet">Подсети</MenuItem>
              <MenuItem value="user">Пользователи</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              fullWidth
              label="Действие"
              value={action}
              onChange={(e) => {
                setAction(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">Все</MenuItem>
              <MenuItem value="create">Создание</MenuItem>
              <MenuItem value="update">Изменение</MenuItem>
              <MenuItem value="delete">Удаление</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Logs Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="50px"></TableCell>
              <TableCell>Дата и время</TableCell>
              <TableCell>Действие</TableCell>
              <TableCell>Тип сущности</TableCell>
              <TableCell>ID сущности</TableCell>
              <TableCell>Пользователь</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Загрузка...
                </TableCell>
              </TableRow>
            ) : !logs || logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Нет записей
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <React.Fragment key={log.id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => toggleRowExpansion(log.id)}
                      >
                        {expandedRows[log.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{formatDate(log.created_at)}</TableCell>
                    <TableCell>
                      <Chip
                        icon={getActionIcon(log.action)}
                        label={getActionLabel(log.action)}
                        color={getActionColor(log.action)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{getEntityTypeLabel(log.entity_type)}</TableCell>
                    <TableCell>{log.entity_id}</TableCell>
                    <TableCell>
                      {log.user ? (
                        <Box>
                          <Typography variant="body2">
                            {log.user.username}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {log.user.full_name || log.user.email}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Система
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                      <Collapse in={expandedRows[log.id]} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                          <Typography variant="h6" gutterBottom component="div">
                            Детали изменений
                          </Typography>
                          {renderChanges(log.changes)}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Записей на странице:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
        />
      </TableContainer>
    </Box>
  );
}

export default AuditLogs;
